import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shared block-visual helpers for the viewer-data lab.
 *
 * Extracted so both the live-data screen (`exp-viewer-data`) and the query
 * canvas (`exp-query-canvas`) render blocks identically — a tile, a canvas
 * node, and a fragment card all derive their media URL, post-it tint, and
 * caption from the same logic. Pure + presentational only; no data fetching.
 */
import React from "react";
import { resolveFileUrl } from "@shared/blocks/file-ref";
/** Deterministic hue (0–359) from an id, for swatch / post-it tints. */
export function hashHue(id) {
    let h = 0;
    for (let i = 0; i < id.length; i++)
        h = (h * 31 + id.charCodeAt(i)) | 0;
    return Math.abs(h) % 360;
}
/** Soft post-it tint derived from a block id, shared by tiles and the modal stage. */
export function postitTint(id) {
    const hue = hashHue(id);
    return {
        background: `hsl(${hue} 65% 94%)`,
        color: `hsl(${hue} 35% 28%)`,
        borderColor: `hsl(${hue} 40% 82%)`,
    };
}
// `Block` is `Omit<union, …>` of its branded ids, which collapses the
// discriminated union — so `block.type` no longer narrows `block.content`. We
// type-guard on `block.type` and cast `content` to the matching variant.
/** Best-effort thumbnail URL for a block; undefined → render a post-it. */
export function blockMediaUrl(block) {
    const thumb = block.metadata.thumbnail;
    if (thumb)
        return resolveFileUrl(thumb);
    if (block.type === "image")
        return resolveFileUrl(block.content.file);
    if (block.type === "video") {
        const t = block.content.thumbnail;
        if (t)
            return t;
    }
    return undefined;
}
/** External URL for a block: a link block's own URL, else any captured source. */
export function blockLinkUrl(block) {
    if (block.type === "link") {
        const url = block.content.url;
        if (url)
            return url;
    }
    return block.metadata.source?.url;
}
export function blockSnippet(block) {
    if (block.type === "text") {
        const t = block.content.text;
        if (t)
            return t;
    }
    return block.summary?.trim() ?? "";
}
export function blockCaption(block) {
    if (block.title && block.title.trim())
        return block.title;
    const snip = blockSnippet(block).replace(/\s+/g, " ").trim();
    if (snip)
        return snip.length > 48 ? `${snip.slice(0, 48)}…` : snip;
    return "Untitled";
}
/**
 * Post-it / link body for a block, shared by the grid tile, the modal stage,
 * and the query-canvas node. Image/video media is rendered by the callers'
 * own image stages — this only handles the text/link surfaces.
 */
export const BlockBody = ({ block, large }) => {
    const tint = postitTint(block.id);
    const postitClass = large
        ? "vlab-block__postit vlab-block__postit--large vld-modal-postit"
        : "vlab-block__postit vld-postit";
    if (block.type === "link") {
        // A title-less link (e.g. a just-captured one still being processed) shows
        // only its host in the bottom corner — no "Untitled" placeholder.
        const hasTitle = (block.title?.trim().length ?? 0) > 0 || blockSnippet(block).trim().length > 0;
        return (_jsxs("div", { className: `${postitClass} vlab-block__postit--link`, style: tint, children: [hasTitle ? _jsx("div", { className: "vlab-block__link-title", children: blockCaption(block) }) : undefined, block.summary ? _jsx("div", { className: "vlab-block__link-desc", children: block.summary }) : undefined, _jsx("div", { className: "vlab-block__link-host", children: block.metadata.source?.domain ?? "" })] }));
    }
    return (_jsx("div", { className: postitClass, style: tint, children: _jsx("div", { className: "vlab-block__text", children: blockSnippet(block) || blockCaption(block) }) }));
};
// ---------------------------------------------------------------------------
// Time formatting — shared by the grid tile and the search results grid.
// ---------------------------------------------------------------------------
/** Absolute date like "Jun 26, 2026", or "—" for missing/invalid input. */
export function formatDate(iso) {
    if (!iso)
        return "—";
    const ms = Date.parse(iso);
    if (Number.isNaN(ms))
        return "—";
    return new Date(ms).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}
/** Wall-clock time like "3:45pm" for an epoch-ms timestamp. */
export function formatClockTime(ms) {
    const d = new Date(ms);
    const h = d.getHours();
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${hour12}:${minutes}${h < 12 ? "am" : "pm"}`;
}
/** Compact relative time like "now", "5m", "2h", "3d", "5mo", "1y" from epoch ms. */
export function relativeTimeMs(ms) {
    const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (s < 60)
        return "now";
    const m = Math.floor(s / 60);
    if (m < 60)
        return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24)
        return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 30)
        return `${d}d`;
    const mo = Math.floor(d / 30);
    if (mo < 12)
        return `${mo}mo`;
    return `${Math.floor(d / 365)}y`;
}
/** Compact relative time from an ISO timestamp. */
export function relativeTime(iso) {
    if (!iso)
        return "";
    const ms = Date.parse(iso);
    if (Number.isNaN(ms))
        return "";
    return relativeTimeMs(ms);
}
/** Best timestamp for "when was this added": surfaced time, else captured. */
export function blockTimestamp(block) {
    return block.surfaceAt ?? block.capturedAt;
}
